import {
  hasExactLength,
  hasLengthBetween,
  hasMaxLength,
  isRequired as isEmpty,
  isValidAccountNumber,
  isValidTaxPayerId,
} from "./UtilityFunction";

import {
  STEP_TRACKER_STATUS,
  VALID_REGX,
  PAYMENT_DETAILS_VALIDATION_MESSAGE,
  BUYER_FORM_INPUTS,
  PAYMENT_AUTHORIZATION_VALIDATION_MESSAGE,
  AUTHORIZATION_FORM_INPUTS,
  PAYMENT_DETAILS_ALPHA_NUMERIC_FIELDS,
  HAS_INVALID_SPECIAL_CHARS,
  AUTHORIZATION_DETAILS_ALPHA_NUMERIC_FIELDS,
  HAS_INVALID_CITY_SPECIAL_CHARS, PHONE_NUMBER_MAX_LENGTH, PHONE_NUMBER_MIN_LENGTH, ONLY_NUMERIC_REGEX,
} from "src/constants/achEnrollment";

import { clearRequestId } from "../reducers/slices/RequestIdSlice";
import store from "src/store/store";
import {
  ERROR_CODE_INVALID_SESSION,
  ERROR_CODE_SESSION_EXISTS,
  VALIDATION_STATUS_FAILED
} from "src/constants/errors/ApiErrorCode";
import {pagePath} from "src/app/Routers";
import { clearSession } from "src/api/Api";

// prettier-ignore
export const isValidEmail = (param: string = "") => VALID_REGX.Email.test(param);

export const isValidZipCode = (param: any) => {
  return VALID_REGX.ZipCode.test(param);
};

// prettier-ignore
export const isValidAccessCode = (param: any) => {
  return VALID_REGX.AccessCode.test(param);
};

export const generateStepStatuses = (completed: number, pending: number) => {
  // prettier-ignore
  const completedSteps = new Array(completed).fill(STEP_TRACKER_STATUS.Completed);
  const pendingSteps = new Array(pending).fill(STEP_TRACKER_STATUS.Default);

  return [...completedSteps, ...pendingSteps];
};

export const groupBuyersById = (selectedBuyers: any, initialBuyerData = {}) => {
  return selectedBuyers.reduce((accumulator: any, buyer: any) => {
    accumulator[buyer.buyer_id] = initialBuyerData;

    return accumulator;
  }, {});
};

export const isObjectHasAllValues = (param = {}) => {
  for (let key in param) {
    if (typeof  param[key] === "string" && param[key].trim() === "") {
      return false;
    }
  }

  return true;
};

const validateTaxPayerId = (inputValue: string, formField: string) => {
  return !isValidTaxPayerId(inputValue)
    ? PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].invalidFormatMsg
    : "";
};

const validateEmailAddress = (inputValue: string, formField: string) => {
  return !isValidEmail(inputValue)
    ? PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].invalidFormatMsg
    : "";
};

const validateMaxLength = (
  inputValue: string,
  formField: string,
  maxLength: number
) => {
  return hasMaxLength(inputValue, maxLength)
    ? PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].invalidFormatMsg
    : "";
};

const validateLengthBetween = (
  inputValue: string,
  formField: string,
  min: number,
  max: number
) => {
  return !hasLengthBetween(inputValue, min, max)
    ? PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].invalidFormatMsg
    : "";
};

const validateExactLength = (
  inputValue: string,
  formField: string,
  exactLength: number
) => {
  return !hasExactLength(inputValue, exactLength)
    ? PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].invalidFormatMsg
    : "";
};

const validateAccountNumberMatch = (
  inputValue: string,
  relatedValue: string,
  formField: string
) => {
  return inputValue !== relatedValue
    ? PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].invalidFormatMsg
    : "";
};

const getInvalidErrorMessages = (
  inputValue: string,
  formField: string,
  relatedValue: any
) => {
  switch (formField) {
    case BUYER_FORM_INPUTS.taxPayerId:
      return validateTaxPayerId(inputValue, formField);

    case BUYER_FORM_INPUTS.emailAddress:
      return validateEmailAddress(inputValue, formField);

    case BUYER_FORM_INPUTS.financialInstituteName:
      return validateMaxLength(inputValue, formField, 100);

    case BUYER_FORM_INPUTS.financialInstituteAddress:
      return validateMaxLength(inputValue, formField, 256);

    case BUYER_FORM_INPUTS.city:
      return validateMaxLength(inputValue, formField, 50);

    case BUYER_FORM_INPUTS.state:
      return !hasMaxLength(inputValue, 256)
        ? PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].invalidFormatMsg
        : "";

    case BUYER_FORM_INPUTS.zipCode:
      return validateLengthBetween(inputValue, formField, 5, 8);

    case BUYER_FORM_INPUTS.routingNumber:
      return validateExactLength(inputValue, formField, 9);

    case BUYER_FORM_INPUTS.accountNumber:
      return !isValidAccountNumber(inputValue)
        ? PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].invalidFormatMsg
        : "";

    case BUYER_FORM_INPUTS.confirmAccountNumber:
      return validateAccountNumberMatch(inputValue, relatedValue, formField);

    default:
      return "";
  }
};

export const validatePhoneNumber = (inputValue: string, formField: string) => {
  if (!ONLY_NUMERIC_REGEX.test(inputValue)) {
    return PAYMENT_AUTHORIZATION_VALIDATION_MESSAGE[formField].invalidFormatMsg;
  }

  if (inputValue.length < PHONE_NUMBER_MIN_LENGTH || inputValue.length > PHONE_NUMBER_MAX_LENGTH) {
    return PAYMENT_AUTHORIZATION_VALIDATION_MESSAGE[formField].invalidFormatMsg;
  }

  return "";
};

const getInvalidAuthorizationErrorMessages = (
  inputValue: string,
  formField: string
) => {
  switch (formField) {
    case AUTHORIZATION_FORM_INPUTS.name:
      return hasMaxLength(inputValue, 40)
        ? PAYMENT_AUTHORIZATION_VALIDATION_MESSAGE[formField].invalidFormatMsg
        : "";

    case AUTHORIZATION_FORM_INPUTS.emailAddress:
      return !isValidEmail(inputValue)
        ? PAYMENT_AUTHORIZATION_VALIDATION_MESSAGE[formField].invalidFormatMsg
        : "";

    case AUTHORIZATION_FORM_INPUTS.phoneNumber:
      return validatePhoneNumber(inputValue, formField);

    default:
      return "";
  }
};

// prettier-ignore
export const hasAllowedSpecialChars = (inputValue: any) => VALID_REGX.AllowedSpecialChars.test(inputValue);
// prettier-ignore
export const isValidCity = (inputValue: any) => VALID_REGX.City.test(inputValue);

export const getPaymentDetailsErrorMsg = (
  inputValue: any,
  formField: any,
  relatedValue: any
) => {
  if (isEmpty(inputValue)) {
    return PAYMENT_DETAILS_VALIDATION_MESSAGE[formField].emptyMsg;
  }

  // prettier-ignore
  if (PAYMENT_DETAILS_ALPHA_NUMERIC_FIELDS.includes(formField) && !isEmpty(inputValue) && !hasAllowedSpecialChars(inputValue)) {
    return HAS_INVALID_SPECIAL_CHARS;
  }

  // prettier-ignore
  if (formField === BUYER_FORM_INPUTS.city && !isEmpty(inputValue) && !isValidCity(inputValue)) {
    return HAS_INVALID_CITY_SPECIAL_CHARS;
  }

  return getInvalidErrorMessages(inputValue, formField, relatedValue);
};

export const getPaymentAuthorizationErrorMsg = (
  inputValue: any,
  formField: any
) => {
  if (isEmpty(inputValue)) {
    return PAYMENT_AUTHORIZATION_VALIDATION_MESSAGE[formField].emptyMsg;
  }

  // prettier-ignore
  if (AUTHORIZATION_DETAILS_ALPHA_NUMERIC_FIELDS.includes(formField) && !isEmpty(inputValue) && !hasAllowedSpecialChars(inputValue)) {
    return HAS_INVALID_SPECIAL_CHARS;
  }

  return getInvalidAuthorizationErrorMessages(inputValue, formField);
};




export const handleRoutingBasedOnError = (response: any, navigate:any) => {


  if (response?.data?.status === VALIDATION_STATUS_FAILED && response?.data?.errors?.length > 0) {
    const errorCode = response?.data?.errors[0].errorCode;


    store.dispatch(clearRequestId());

    switch (errorCode) {
      case ERROR_CODE_SESSION_EXISTS:
        navigate(pagePath.exisistingSessionPage);
        break;
      case ERROR_CODE_INVALID_SESSION:
        navigate(pagePath.exisistingSessionPage);
        break;
      default:
        navigate(pagePath.errorPage);
        break;
    }
  }
  else {
    navigate(pagePath.errorPage);
  }
};

export const clearLocalStorageItems = (keys: string[]) => {
  keys.forEach((key) => localStorage.removeItem(key));
};


export const clearSessionUtil = async (payloadData: any) => {
  if (!payloadData?.emailId) {
    console.error("Invalid payload: emailId is required");
    return;
  }

  const payload = {
    emailId: payloadData.emailId,
    source: "ACH",
  };

  try {
    const response = await clearSession(payload);
    return response.data; // Return the response data for further handling
  } catch (error) {
    console.error("Error clearing session:", error);
    throw error; // Rethrow the error for the caller to handle
  }
};